import { useState } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { instrumentPath, instrumentsPath } from '@/shared/lib/routes'
import { buildInstrumentFormPayload } from '@/features/instrument-form/buildInstrumentFormPayload'
import { resolveInstrumentEditCancelPath } from '@/features/instrument-form/resolveInstrumentEditCancelPath'
import {
  instrumentTypesFilterQuery,
  resolveReturnFilters,
  resolveReturnTo,
} from '@/shared/lib/instrument-edit-return-to'
import {
  InstrumentFormFields,
  type InstrumentFormData,
  type InstrumentFormErrors,
  type InstrumentFormSelectOption,
} from '@/features/instrument-form/InstrumentFormFields'
import { useFollowParentUnitSchedulePreview } from '@/entities/instruments/useFollowParentUnitSchedulePreview'
import { parseOptionalId } from '@/features/instrument-form/parseOptionalId'
import type { WebInstrumentForm } from '@/shared/types/web'

interface Props {
  instrument: WebInstrumentForm | null
  isEdit: boolean
  instrumentTypeOptions: InstrumentFormSelectOption[]
  siteOptions: InstrumentFormSelectOption[]
  departmentOptions: InstrumentFormSelectOption[]
  unitOptions: InstrumentFormSelectOption[]
  customDaySchedule: WeekdaySchedule
  parentDaySchedule: WeekdaySchedule
  preselectedInstrumentTypeId?: number | null
  /** Create-only seeds from scoped `?unit_id=` (mirrors Units `departmentId` seed). */
  unitId?: number | null
  siteId?: number | null
  departmentId?: number | null
  /** Raw origin from the server — re-allowlisted client-side before use (REBUILD-381). */
  returnTo?: unknown
  /** Raw Instrument Types filters from the server — re-allowlisted client-side before use
   * (REBUILD-388/JW 386). */
  returnFilters?: unknown
  /** REBUILD-531: whether the tenant has open logging on (gates the Log Authentication select). */
  tenantOpenLoggingEnabled?: boolean
  [key: string]: unknown
}

const InstrumentForm = () => {
  const {
    instrument,
    isEdit,
    instrumentTypeOptions,
    siteOptions,
    departmentOptions,
    unitOptions,
    customDaySchedule,
    parentDaySchedule,
    preselectedInstrumentTypeId,
    unitId: seededUnitId,
    siteId: seededSiteId,
    departmentId: seededDepartmentId,
    returnTo,
    returnFilters,
    tenantOpenLoggingEnabled,
  } = usePage<Props>().props
  const { errors: pageErrors } = usePage<{ errors?: InstrumentFormErrors }>().props

  // Create seeds from ?unit_id=; clone uses instrument prefill unit (isEdit is false).
  const createSeedUnitId = !isEdit
    ? (seededUnitId ?? parseOptionalId(instrument?.unitId?.toString()) ?? null)
    : null

  const form = useForm<InstrumentFormData>({
    instrumentLabel: instrument?.instrumentLabel ?? '',
    serialNumber: instrument?.serialNumber ?? '',
    barcodeId: instrument?.barcodeId ?? '',
    cost: instrument?.cost ?? '',
    dateOfPurchase: instrument?.dateOfPurchase?.split('T')[0] ?? '',
    instrumentTypeId:
      instrument?.instrumentTypeId?.toString() ?? preselectedInstrumentTypeId?.toString() ?? '',
    followParentSchedule: instrument?.followParentSchedule ?? true,
    customDaySchedule: customDaySchedule ?? ({ ...ALL_DAYS_ON } as WeekdaySchedule),
    siteId: instrument?.siteId?.toString() ?? seededSiteId?.toString() ?? '',
    departmentId: instrument?.departmentId?.toString() ?? seededDepartmentId?.toString() ?? '',
    unitId: instrument?.unitId?.toString() ?? seededUnitId?.toString() ?? '',
    tagIds: instrument?.tagIds ?? [],
    // REBUILD-531: default to the instrument's current override; blank → inherit tenant ('OL').
    logAuthenticationCode: instrument?.logAuthenticationCode ?? 'OL',
  })

  const [clientErrors, setClientErrors] = useState<InstrumentFormErrors>({})
  // Drop stale server and client errors for required fields the user has already
  // filled so a prior instrumentTypeId failure does not linger after selection
  // (clientErrors otherwise take precedence until the next submit).
  const stripFilledRequiredFieldErrors = (source: InstrumentFormErrors): InstrumentFormErrors => {
    const next: InstrumentFormErrors = { ...source }
    if (form.data.instrumentLabel?.trim()) delete next.instrument_label
    if (form.data.instrumentTypeId) delete next.instrumentTypeId
    if (form.data.siteId) delete next.siteId
    if (form.data.departmentId) delete next.departmentId
    if (form.data.unitId) delete next.unitId
    return next
  }
  const errors: InstrumentFormErrors = {
    ...stripFilledRequiredFieldErrors((pageErrors as InstrumentFormErrors | undefined) ?? {}),
    ...stripFilledRequiredFieldErrors(clientErrors),
  }
  // REBUILD-103: when the form is opened from an instrument type page the type is
  // preselected and must stay locked, so the user cannot pick the wrong type.
  const isTypeLocked = !isEdit && preselectedInstrumentTypeId != null
  const canSubmit = useCan(
    Resources.INSTRUMENT_MAINTENANCE,
    isEdit ? Actions.UPDATE : Actions.CREATE,
  )

  const selectedUnitId = parseOptionalId(form.data.unitId)
  // Exhausted preview: bump to retry while Follow stays ON (single Follow toggle).
  const [previewRetryNonce, setPreviewRetryNonce] = useState(0)
  const { effectiveParentPreview, parentPreviewPending, parentPreviewFailed } =
    useFollowParentUnitSchedulePreview({
      enabled: form.data.followParentSchedule,
      unitId: selectedUnitId,
      parentDaySchedule,
      initialUnitId: isEdit ? parseOptionalId(instrument?.unitId?.toString()) : createSeedUnitId,
      retryNonce: previewRetryNonce,
    })

  useBreadcrumbs([
    { label: t('nav.instruments'), href: instrumentsPath() },
    ...(isEdit && instrument
      ? [
          { label: instrument.instrumentLabel, href: instrumentPath(instrument.id) },
          { label: t('instruments.form.editTitle') },
        ]
      : [{ label: t('instruments.form.newTitle') }]),
  ])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const nextErrors: InstrumentFormErrors = {}
    if (!form.data.instrumentLabel?.trim())
      nextErrors.instrument_label = [t('instruments.form.labelRequired')]
    if (!isEdit && !form.data.instrumentTypeId)
      nextErrors.instrumentTypeId = [t('instruments.form.typeRequired')]
    if (!form.data.siteId) nextErrors.siteId = [t('instruments.form.siteRequired')]
    if (!form.data.departmentId)
      nextErrors.departmentId = [t('instruments.form.departmentRequired')]
    if (!form.data.unitId) nextErrors.unitId = [t('instruments.form.unitRequired')]
    setClientErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    form.transform((data) => buildInstrumentFormPayload(data, isEdit))
    if (isEdit && instrument?.id) {
      // Thread the allowlisted origin (and its filters) through to
      // Web::InstrumentsController#update so a successful Save returns to the same place,
      // with the same Instrument Types filters, that Cancel does (REBUILD-381, REBUILD-388).
      const resolvedReturnTo = resolveReturnTo(returnTo)
      form.patch(
        resolvedReturnTo
          ? instrumentPath(instrument.id, {
              return_to: resolvedReturnTo,
              ...instrumentTypesFilterQuery(resolveReturnFilters(returnFilters)),
            })
          : instrumentPath(instrument.id),
      )
    } else {
      form.post(instrumentsPath())
    }
  }

  const cancelPath = resolveInstrumentEditCancelPath({
    isEdit,
    instrumentId: instrument?.id,
    returnTo,
    returnFilters,
  })

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-6">
          {isEdit ? t('instruments.form.editTitle') : t('instruments.form.newTitle')}
        </h1>

        {errors.base && (
          <div className="mb-4 rounded border border-destructive bg-destructive/10 px-4 py-3">
            {(Array.isArray(errors.base) ? errors.base : [errors.base]).map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <InstrumentFormFields
            form={form}
            errors={errors}
            instrumentTypeOptions={instrumentTypeOptions}
            siteOptions={siteOptions}
            departmentOptions={departmentOptions}
            unitOptions={unitOptions}
            parentDaySchedule={effectiveParentPreview}
            parentPreviewPending={parentPreviewPending}
            parentPreviewFailed={parentPreviewFailed}
            onParentPreviewRetry={() => setPreviewRetryNonce((nonce) => nonce + 1)}
            isTypeLocked={isTypeLocked}
            hideTypeField={isEdit}
            initialTags={instrument?.tags ?? []}
            tenantOpenLoggingEnabled={tenantOpenLoggingEnabled ?? false}
          />

          {!canSubmit && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canSubmit && (
              <Button type="submit" disabled={form.processing}>
                {isEdit ? t('instruments.form.submitUpdate') : t('instruments.form.submitCreate')}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => router.visit(cancelPath)}>
              {t('instruments.form.cancelButton')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InstrumentForm
